import React from 'react';
import { FeatureLink } from '../../components/FeatureLink';
import { Library, ShieldCheck, Star, ArrowRight } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { ToolLibraryEntry } from '../../types/aai';
import { useAsync } from './useAsync';
import { Loading, ErrorNote } from './components';
import { Card, MonoLabel, PageHeader } from './dashboards/ui';

// Module 2 — the participant tool library. Read-only browse of the tools your
// organization has evaluated (published evaluations). No research is triggered
// here: this is "library browsing, not research spend."
const SOC2_LABEL: Record<string, string> = {
  yes: 'SOC 2', in_progress: 'SOC 2 in progress', no: 'No SOC 2', unknown: 'SOC 2 unknown',
};

const ToolCard: React.FC<{ t: ToolLibraryEntry }> = ({ t }) => (
  <Card>
    <div className="flex items-start gap-3">
      <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 grid place-items-center font-sans font-bold text-[14px] shrink-0">
        {(t.name || '?').charAt(0).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] font-semibold text-gray-900">{t.name}</span>
          {t.cleared && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold">
              <ShieldCheck size={11} /> Cleared for use
            </span>
          )}
          {typeof t.rating === 'number' && (
            <span className="inline-flex items-center gap-0.5 text-[11.5px] text-amber-600 font-semibold">
              <Star size={11} className="fill-amber-400 text-amber-400" /> {t.rating}/5
            </span>
          )}
        </div>
        {t.what_it_does && <p className="text-[13px] text-gray-600 mt-1 leading-relaxed">{t.what_it_does}</p>}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {t.pricing_tier && <span className="rounded-full bg-gray-100 text-gray-600 px-2.5 py-0.5 text-[11px] font-medium">{t.pricing_tier}</span>}
          {t.soc2 && <span className="rounded-full bg-gray-100 text-gray-600 px-2.5 py-0.5 text-[11px] font-medium">{SOC2_LABEL[t.soc2] || t.soc2}</span>}
        </div>
        {t.data_flow && <p className="text-[11.5px] text-gray-400 mt-2">Data: {t.data_flow}</p>}
        {t.recommendation && (
          <p className="text-[12px] text-gray-500 mt-2 border-l-2 border-gray-200 pl-2.5 leading-relaxed">
            {t.recommendation}
          </p>
        )}
      </div>
    </div>
  </Card>
);

const ToolLibraryPage: React.FC = () => {
  const { data, loading, error } = useAsync<{ tools: ToolLibraryEntry[] }>(
    () => aaiApi.getToolLibrary(), 'Could not load the tool library.');
  if (loading) return <Loading label="Loading the tool library…" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  const tools = data.tools ?? [];
  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Tool library"
        subtitle="The AI tools your organization has evaluated — what they do, cost, and security. Browse freely; nothing here costs anything."
      />
      {tools.length === 0 ? (
        <Card className="text-center py-10">
          <span className="mx-auto mb-4 grid place-items-center h-14 w-14 rounded-full bg-gray-100 text-gray-400">
            <Library size={24} />
          </span>
          <p className="text-[15px] font-semibold text-gray-900 mb-1">No tools in the library yet</p>
          <p className="text-[13px] text-gray-500 max-w-sm mx-auto mb-5">
            Your AI champion is evaluating tools. In the meantime, see what's approved on What's Allowed.
          </p>
          <FeatureLink to="/whats-allowed"
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold px-4 py-2">
            Go to What's Allowed <ArrowRight size={14} />
          </FeatureLink>
        </Card>
      ) : (
        <div className="space-y-3">
          {tools.map((t, i) => <ToolCard key={`${t.name}-${i}`} t={t} />)}
        </div>
      )}
    </div>
  );
};

export default ToolLibraryPage;
